import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import dayjs from "dayjs";
import _ from "lodash";
import ReactDOMServer from "react-dom/server";
import { connect } from "react-redux";
import styled from "styled-components";

import { shortMonthAndYear, shortMonthDayAndYear } from "@similarweb/pro-logic-components";
import { colorsPalettes } from "@similarweb/styles";
import { LegendWithValue } from "@similarweb/ui-components";

import { swSettings } from "app/common/services/swSettings";
import { useSelectorTrigger } from "app/components/filters-bar/useSelectorTrigger";
import { AutoCompareLegends } from "app/components/React/Legends/AutoCompareLegends";
import type { ILegendItems } from "app/components/React/Legends/legendsTypes";
import { WidgetSubtitle } from "app/components/React/Widgets/WidgetsSubtitle";
import { TitleContainer } from "app/components/React/Widgets/WidgetsTop";
import { NoPrintContainer, WeShare } from "app/components/weshare/weshare";
import { resolveTimeGranularity } from "app/components/widget/widget-utilities/time-granularity";
import { WidgetFooter } from "app/components/widget/WidgetFooter";
import { CHART_COLORS } from "app/constants/ChartColors";
import { useAddonsState } from "app/contexts/AddonStateContext/AddonStateContext";
import { AddOnType } from "app/contexts/AddonStateContext/const";
import { i18nFilter } from "app/filters/i18nFilter";
import {
    abbrNumberVisitsWithNullFilter,
    abbrNumberWithNASupportVisitsFilter,
    minVisitsAbbrFilter,
} from "app/filters/ngFilters";
import { HDBanner } from "app/marketing/components/common/banners/HDBanner/HDBanner";
import type { IHDBannerCtaState } from "app/marketing/components/common/banners/HDBanner/useHDBannerCta";
import { NoData } from "app/pages/keyword-analysis/TotalTrafficPage/Components/UtilityComponents";
import { MetricContainer, MetricsRow } from "app/pages/keyword-analysis/TotalTrafficPage/StyledComponents";
import { SwitcherGranularityContainer } from "app/pages/website-analysis/components/SwitcherGranularityContainer";
import { commonOverTimeChartConfigCompare } from "app/pages/website-analysis/incoming-traffic/commonOverTime";
import { TrafficOverTimeChartToolTip, StyledSpan } from "app/pages/website-analysis/incoming-traffic/StyledComponents";
import { websiteWorldWideOverviewLogic } from "app/pages/website-performance/helpers/websitePerformanceHelpers";
import DurationService from "app/services/DurationService";
import { DefaultFetchService } from "app/services/fetchService";
import { useSimilarSites } from "app/services/sitesService/hooks/useSimilarSites";
import type { ISimilarInfoItem } from "app/services/sitesService/types";
import { allTrackers } from "app/services/track/track";
import { getDateAsText } from "app/UtilitiesAndConstants/UtilityFunctions/timeAndDate";
import BoxTitle from "pro-features/components/BoxTitle/src/BoxTitle";
import Chart from "pro-features/components/Chart/src/Chart";
import combineConfigs from "pro-features/components/Chart/src/combineConfigs";
import { CircularProgress } from "pro-features/components/Chart/src/components/annotations/Components/CircularProgress";
import { ChartIds } from "pro-features/components/Chart/src/components/annotations/Helpers/ChartIdsHelper";
import monthlyIntervalConfig from "pro-features/components/Chart/src/configs/granularity/monthlyIntervalConfig";
import noLegendConfig from "pro-features/components/Chart/src/configs/legend/noLegendConfig";
import xAxisCrosshair from "pro-features/components/Chart/src/configs/xAxis/xAxisCrosshair";
import xAxisLabelsConfig from "pro-features/components/Chart/src/configs/xAxis/xAxisLabelsConfig";
import yAxisLabelsConfig from "pro-features/components/Chart/src/configs/yAxis/yAxisLabelsConfig";
import { FlexRow } from "pro-features/styled components/StyledFlex/src/StyledFlex";

import { WWOTrafficVisitsOverTimePopover } from "./WWOTrafficVisitsOverTimePopover";

const fetchService = DefaultFetchService.getInstance();
const i18n = i18nFilter();
const minVisitsAbbr = minVisitsAbbrFilter();
const visitsAbbrNumber = abbrNumberWithNASupportVisitsFilter();

const CHART_TYPE = "line";
const DEFAULT_ENDPOINTS_CONTROLLERZ = "WebsiteOverview";
const EMPTY_SIMILAR_SITES: ISimilarInfoItem[] = [];

const legendsEndpoint = (controller = DEFAULT_ENDPOINTS_CONTROLLERZ) =>
    `widgetApi/${controller}/EngagementVisits/Table`;
const graphEndpoint = (controller = DEFAULT_ENDPOINTS_CONTROLLERZ) => `widgetApi/${controller}/EngagementVisits/Graph`;
const TitleKey = "wwo.visits.graph";
const ToolTipKey = "wwo.visits.graph.tooltip";

type TimeGranularity = "Daily" | "Weekly" | "Monthly";

enum timeGranularityToIndexMap {
    Daily = 0,
    Weekly = 1,
    Monthly = 2,
}

const TitleContainerStyled = styled(TitleContainer)`
    display: flex;
    justify-content: space-between;
    border-bottom: 1px solid ${colorsPalettes.carbon[100]};
    padding: 24px 14px 20px 24px;

    > div:first-child {
        display: flex;
        flex-direction: column;

        > div {
            margin-bottom: 8px;
        }
    }
`;

const TooltipDataPoint = styled.div<{ color: string }>`
    position: relative;
    margin-bottom: 6px;
    margin-left: 12px;

    &::before {
        content: "";
        position: absolute;
        width: 6px;
        height: 6px;
        border-radius: 50%;
        top: 50%;
        left: -12px;
        transform: translateY(-50%);
        background-color: ${(props) => props.color};
    }
`;

const LegendsContainer = styled(FlexRow)`
    margin-bottom: 24px;
`;

const CardBodyWrapper = styled.div`
    height: calc(100% - 184px);
    padding: 24px 24px 0;
`;

const CircularProgressStyled = styled(CircularProgress)`
    position: relative;
`;

const SwitcherGranularityContainerStyled = styled(SwitcherGranularityContainer)`
    display: block;
`;

const chartTooltip =
    (
        siteList: string[],
        timeGranularity: TimeGranularity,
        isAlternativeSource = false,
        /*
         * The add-on availability arrives after the config is built, and the config outlives it in
         * state, so the formatter has to read the state when the tooltip opens rather than close over it
         */
        getHdBannerCtaState?: () => IHDBannerCtaState,
    ) =>
    () => ({
        tooltip: {
            stickOnContact: true,
            /*
             * The tooltip content brings its own padding, and the shared default would add a second
             * one around it that the full width banner cannot reach over
             */
            padding: 0,
            style: {
                pointerEvents: "auto",
            },
            formatter() {
                const dateFromTitle = dayjs
                    .utc(this.x)
                    .format(timeGranularity !== "Monthly" ? shortMonthDayAndYear : shortMonthAndYear);

                let dateToTitle: string;
                if (timeGranularity === "Weekly") {
                    const points: any = _.last(this.points[0].series.points);
                    const isLast: boolean = points.x === this.x;
                    const dateTo = dayjs.utc(this.x).add(6, "days");
                    const newDateTo =
                        isLast && dateTo.month() !== dayjs.utc(this.x).month()
                            ? dayjs.utc(this.x).endOf("month").startOf("day").utc()
                            : dateTo;
                    dateToTitle = newDateTo.format(shortMonthDayAndYear);
                }

                return ReactDOMServer.renderToString(
                    <TrafficOverTimeChartToolTip>
                        <strong>
                            {timeGranularity === "Weekly"
                                ? `from ${dateFromTitle} to ${dateToTitle}`
                                : `${i18n("wwo.visits.graph.tooltip.title")} - ${dateFromTitle}`}
                        </strong>
                        {this.points.map((point, i) => (
                            <TooltipDataPoint
                                key={i}
                                color={point.color}
                            >
                                <StyledSpan>{point.series.name}: </StyledSpan>
                                <StyledSpan style={{ color: point.color }}>
                                    {isAlternativeSource ? visitsAbbrNumber(point.y) : minVisitsAbbr(point.y)}
                                </StyledSpan>
                            </TooltipDataPoint>
                        ))}
                        {getHdBannerCtaState ? (
                            <HDBanner
                                ctaState={getHdBannerCtaState()}
                                dataAutomation="traffic-visits-over-time-hd-banner-cta"
                            />
                        ) : null}
                    </TrafficOverTimeChartToolTip>,
                );
            },
        },
    });
const getChartConfig = (
    siteList: string[],
    timeGranularity: TimeGranularity,
    isAlternativeSource = false,
    getHdBannerCtaState?: () => IHDBannerCtaState,
) => {
    const format = timeGranularity === "Monthly" ? "MMM YY" : "DD MMM";
    const xAxisFormatter = ({ value }) => {
        return dayjs.utc(value).format(format);
    };
    const config = combineConfigs(
        {
            type: CHART_TYPE,
            xAxisFormatter,
            yAxisFormatter: ({ value }) =>
                isAlternativeSource ? abbrNumberVisitsWithNullFilter()(value) : minVisitsAbbr(value),
        },
        [
            monthlyIntervalConfig,
            noLegendConfig,
            yAxisLabelsConfig,
            xAxisLabelsConfig,
            xAxisCrosshair,
            commonOverTimeChartConfigCompare,
            chartTooltip(siteList, timeGranularity, isAlternativeSource, getHdBannerCtaState),
            {
                colors: CHART_COLORS.main,
                chart: {
                    height: 308,
                    type: CHART_TYPE,
                    spacingTop: 10,
                    plotBackgroundColor: "transparent",
                    events: {},
                },
                plotOptions: {
                    line: {
                        lineWidth: 2,
                        connectNulls: false,
                        marker: {
                            enabled: timeGranularity !== "Daily",
                            symbol: "circle",
                        },
                    },
                    series: {
                        states: {
                            hover: {
                                enabled: true,
                            },
                        },
                    },
                },
                yAxis: {
                    gridLineWidth: 0.5,
                    showFirstLabel: true,
                    showLastLabel: true,
                    reversed: false,
                    gridZIndex: 2,
                    reversedStacks: true,
                    tickPixelInterval: 50,
                    labels: {
                        style: {
                            textTransform: "uppercase",
                            fontSize: "11px",
                            color: "#919191",
                        },
                    },
                },
                xAxis: {
                    type: "datetime",
                    gridLineWidth: 0,
                    gridLineDashStyle: "dash",
                    tickLength: 5,
                    crosshair: {
                        width: 1,
                        color: colorsPalettes.sky[500],
                        dashStyle: "dash",
                    },
                    labels: {
                        style: {
                            textTransform: "capitalize",
                            fontSize: "11px",
                            color: "#919191",
                            textOverflow: "none",
                            whiteSpace: "nowrap",
                        },
                    },
                    minPadding: 0,
                    maxPadding: 0,
                    ...(["Daily", "Weekly"].includes(timeGranularity) ? { tickInterval: null } : {}),
                },
            },
        ],
    );
    return config;
};

const popoverStateName = "visits.over.time.popover";
const todayAsText = getDateAsText();
const savePopoverStateInLocalStorage = () => {
    localStorage.setItem(popoverStateName, todayAsText);
};

const getPopoverState = (): boolean => localStorage.getItem(popoverStateName) !== todayAsText;

const WWOTrafficVisitsOverTime = ({
    isAlternativeSource,
    getCtaLink,
    unlockHook = null,
    hideFooter = false,
    getHdBannerCtaState,
}) => {
    const { params } = useCurrentStateAndParams();
    const { country, key, isWWW, webSource, duration } = params;
    const { isWWWithoutPermissions } = websiteWorldWideOverviewLogic(country);
    const { selectorHandlers } = useSelectorTrigger("traffic-visits-over-time");
    const siteList = useRef<string[]>(null);
    const [chartHasNoData, setChartHasNoData] = useState(false);
    const [mainLegendHasNoData, setMainLegendHasNoData] = useState(false);
    const [legendItems, setLegendItems] = useState<ILegendItems>([]);
    const [chartData, setChartData] = useState<any>({});
    const [chartRawData, setChartRawData] = useState<any>({});
    const [chartConfig, setChartConfig] = useState<any>({});
    const [sitesVisibility, setSitesVisibility] = useState<Record<string, boolean>>({});
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const chartRef = useRef<HTMLDivElement | null>(null);
    const { from, to, isWindow } = useMemo(() => DurationService.getDurationData(duration).forAPI, [duration]);
    const [selectedTimeGranularity, setSelectedTimeGranularity] = useState<TimeGranularity>(
        resolveTimeGranularity(duration),
    );
    const isCompare = useMemo(() => key.split(",").length > 1, [key]);
    const ctaHref = getCtaLink({
        country,
        key,
        isWWW: "*",
        duration,
        webSource,
        selectedWidgetTab: "Visits",
    });

    const [isPopoverOpen, setIsPopoverOpen] = useState(getPopoverState());

    const { hasAddonsHub, getIsAddonAvailable } = useAddonsState();

    const shouldFetchSimilarSites = !isCompare && !isAlternativeSource && !!key;
    const { data: similarSitesData = EMPTY_SIMILAR_SITES } = useSimilarSites(
        key as string,
        {
            limit: 4,
            country: country as string,
            webSource: webSource as string,
        },
        {
            enabled: shouldFetchSimilarSites,
        },
    );

    const shouldShowPopover = useMemo(() => {
        if (
            !hasAddonsHub ||
            !getIsAddonAvailable(AddOnType.HistoricalData) ||
            !swSettings.current?.resources?.SnapshotInterval?.startdate ||
            !swSettings.current?.resources?.SnapshotInterval?.enddate
        ) {
            return false;
        }

        const startDate = new Date(swSettings.current.resources.SnapshotInterval.startdate).getTime();
        const endDate = new Date(swSettings.current.resources.SnapshotInterval.enddate).getTime();
        const intervalLength = endDate - startDate;

        return intervalLength < 1000 * 60 * 60 * 24 * 364;
    }, [getIsAddonAvailable, hasAddonsHub]);

    const onPopoverCloseClicked = useCallback(() => {
        allTrackers.dispatchTrackEvent("button", "close", "add-on/visits over time");
        savePopoverStateInLocalStorage();
        setIsPopoverOpen(false);
    }, []);

    const timeGranularities = useMemo(
        () => [
            {
                value: "Daily",
                title: "D",
                disabled: isAlternativeSource,
            },
            {
                value: "Weekly",
                title: "W",
                disabled: isAlternativeSource,
            },
            {
                value: "Monthly",
                title: "M",
                disabled: isWindow,
            },
        ],
        [isAlternativeSource, isWindow],
    );

    const fetchData = async (queryParams) => {
        try {
            siteList.current = [];
            // if no competitors were selected
            if (!isCompare && !isAlternativeSource && similarSitesData.length > 0) {
                siteList.current = similarSitesData.map((site) => site.Domain);
                siteList.current.unshift(key); // add main site
            } else {
                siteList.current = key.split(",");
            }
            setChartConfig(
                getChartConfig(siteList.current, selectedTimeGranularity, isAlternativeSource, getHdBannerCtaState),
            );
            queryParams.keys = siteList.current.join(",");
            // only update legends state if it hadn't been set before
            if (!legendItems.length || legendItems.length !== siteList.current.length) {
                const { Data: legendsData } = await fetchService.get<{
                    Data: { Domain: string; TotalVisits?: number }[];
                }>(
                    legendsEndpoint(isWWWithoutPermissions ? "WorldwideOverviewVisits" : DEFAULT_ENDPOINTS_CONTROLLERZ),
                    queryParams,
                );
                if (siteList.current?.length === legendsData?.length) {
                    setLegendItems(
                        legendsData
                            .filter((website) => typeof website.TotalVisits === "number")
                            .map((website, index) => ({
                                name: website.Domain,
                                data: String(
                                    isAlternativeSource
                                        ? visitsAbbrNumber(website.TotalVisits)
                                        : minVisitsAbbr(website.TotalVisits),
                                ),
                                // all items are visible in compare by default, else only the first item is visible
                                visible: isCompare ? true : index === 0,
                            })),
                    );
                    if (isCompare) {
                        const [mainDomain] = (key as string).split(",");
                        const foundInLegends = legendsData.find((website) => website.Domain === mainDomain);
                        const hasData = foundInLegends?.TotalVisits > 0;

                        setMainLegendHasNoData(!hasData);
                    } else {
                        setMainLegendHasNoData(!legendsData.find((website) => website.Domain === key)?.TotalVisits);
                    }
                }
            }
            // only update sites visibility state if it hadn't been set before
            if (!Object.keys(sitesVisibility).length) {
                let sitesVisibility: Record<string, boolean> = {};
                // if no competitors were selected set only the first to visible
                if (!isCompare) {
                    // only first item should be visible by default
                    sitesVisibility = siteList.current.reduce((acc, curr, i) => ((acc[curr] = i === 0), acc), {});
                } else {
                    // else set all sites to visible
                    sitesVisibility = siteList.current.reduce((acc, curr) => ((acc[curr] = true), acc), {});
                }
                setSitesVisibility(sitesVisibility);
            }
            const { Data: graphData } = await fetchService.get<{ Data: unknown[] }>(
                graphEndpoint(isWWWithoutPermissions ? "WorldwideOverviewVisits" : DEFAULT_ENDPOINTS_CONTROLLERZ),
                queryParams,
            );
            if (siteList.current?.length === Object.keys(graphData)?.length) {
                setChartRawData(graphData);
            }
        } catch (ex) {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        setIsLoading(true);
        const queryParams = {
            country,
            from,
            to,
            timeGranularity: selectedTimeGranularity,
            ShouldGetVerifiedData: false,
            includeSubDomains: isWWW === "*",
            isWindow,
            keys: "", // will get populated later
            webSource,
        };
        fetchData(queryParams);
    }, [selectedTimeGranularity, similarSitesData, isAlternativeSource]);

    useEffect(() => {
        if (Object.keys(chartRawData).length) {
            const trendsDataFormatted = formatChartData(chartRawData, sitesVisibility);
            setChartHasNoData(
                !trendsDataFormatted.length ||
                    trendsDataFormatted.every((item) => item.data?.every((point) => point[1] == null)),
            );
            setChartData(trendsDataFormatted);
            setIsLoading(false);
        }
    }, [sitesVisibility, chartRawData]);

    const formatChartData = (rawData, siteVisibilityList: Record<string, boolean>) => {
        return Object.keys(rawData).map((site, index) => {
            const siteData = rawData[site]?.[webSource === "MobileWeb" ? "Mobile Web" : webSource]?.[0];
            return {
                name: site,
                color: CHART_COLORS.main[index],
                data: siteData?.map((d) => [dayjs.utc(d.Key).valueOf(), d.Value || 0]),
                visible: !!siteVisibilityList[site],
            };
        });
    };

    const toggleWebsiteVisibility = (website: string, isVisible: boolean) => {
        allTrackers.dispatchTrackEvent("Graph Filter", "click", `Over Time Graph/Engagement Overview/${website || ""}`);
        const legendIndex = legendItems.findIndex((item) => item.name === website);
        setLegendItems((state) => {
            state[legendIndex].visible = !isVisible;
            return state;
        });
        setSitesVisibility((state) => ({ ...state, [website]: !isVisible }));
    };

    const onGranularityChange = (granularityIndex: number) => {
        const selectedGranularity = timeGranularityToIndexMap[granularityIndex] as TimeGranularity;
        allTrackers.dispatchTrackEvent(
            "Time Frame Button",
            "click",
            `Over Time Graph/Engagement Overview/${selectedGranularity || ""}`,
        );
        setSelectedTimeGranularity(selectedGranularity);
    };

    const legendsContainOnlyMainSite = legendItems.length <= 1 && legendItems[0]?.name === key;
    const shouldShowLegendsWithValue = isCompare || legendsContainOnlyMainSite;
    const legendsHaveNoData = mainLegendHasNoData || (!shouldShowLegendsWithValue && legendItems.length < 2);

    return (
        <MetricsRow ref={chartRef}>
            <MetricContainer
                width="100%"
                height="584px"
                padding="0px"
            >
                <TitleContainerStyled>
                    <div>
                        <BoxTitle tooltip={i18n(ToolTipKey)}>{i18n(TitleKey)}</BoxTitle>
                        <WidgetSubtitle
                            webSource={webSource}
                            duration={duration}
                            {...selectorHandlers}
                        />
                    </div>
                    <NoPrintContainer>
                        <FlexRow>
                            <div>
                                <SwitcherGranularityContainerStyled
                                    itemList={timeGranularities}
                                    selectedIndex={timeGranularityToIndexMap[selectedTimeGranularity]}
                                    customClass={"CircleSwitcher"}
                                    onItemClick={onGranularityChange}
                                />
                                {shouldShowPopover && isPopoverOpen ? (
                                    <WWOTrafficVisitsOverTimePopover onCloseButtonClicked={onPopoverCloseClicked} />
                                ) : null}
                            </div>
                            <WeShare
                                chartRef={chartRef.current}
                                title={i18n(TitleKey)}
                                description={i18n(TitleKey)}
                                redirectUrl={`https://www.similarweb.com/website/${
                                    isCompare ? key.split(",")[0] : key
                                }/#overview`}
                            />
                        </FlexRow>
                    </NoPrintContainer>
                </TitleContainerStyled>
                <CardBodyWrapper>
                    {isLoading ? (
                        <CircularProgressStyled />
                    ) : (
                        <>
                            {legendsHaveNoData ? null : shouldShowLegendsWithValue ? (
                                <LegendsContainer>
                                    {legendItems.map((item, i) => {
                                        const name = item.name as string;
                                        const itemValue =
                                            item.data || i18n("Usermanagement.ApiManagement.Fields.DefaultValue");
                                        return (
                                            <LegendWithValue
                                                isChecked={item.visible}
                                                key={name}
                                                text={name}
                                                value={itemValue}
                                                labelColor={CHART_COLORS.main[i]}
                                                onClick={() => toggleWebsiteVisibility(name, item.visible)}
                                            />
                                        );
                                    })}
                                </LegendsContainer>
                            ) : (
                                <LegendsContainer>
                                    <AutoCompareLegends
                                        legendItems={legendItems}
                                        toggleSeries={(item) => toggleWebsiteVisibility(item.name, item.visible)}
                                    />
                                </LegendsContainer>
                            )}
                            {chartHasNoData || (legendsHaveNoData && !isAlternativeSource) ? (
                                <NoData paddingTop="120px" />
                            ) : (
                                <Chart
                                    type={CHART_TYPE}
                                    config={chartConfig}
                                    data={chartData}
                                    chartIdForAnnotations={ChartIds["WebsitePerformanceVisitsOverTime"]}
                                />
                            )}
                            {hideFooter ? null : (
                                <NoPrintContainer>
                                    <WidgetFooter
                                        {...ctaHref}
                                        text="wwo.visits.over.time.button"
                                        trackingLabel="Traffic And Engagement/See traffic over time"
                                        unlockHook={unlockHook}
                                    />
                                </NoPrintContainer>
                            )}
                        </>
                    )}
                </CardBodyWrapper>
            </MetricContainer>
        </MetricsRow>
    );
};
const mapStateToProps = ({ common: { alternativeSourceState } }) => {
    return {
        isAlternativeSource: alternativeSourceState.isAlternativeSource,
    };
};

export default connect(mapStateToProps)(WWOTrafficVisitsOverTime);
